/* ==========================================================================
   SAVA — header behavior
   Scroll states, drawers (search / menu / cart), focus trap, cart count.
   ========================================================================== */
(function () {
  'use strict';

  var header = document.querySelector('[data-yv-header]');
  if (!header) return;

  var isHome = header.getAttribute('data-home') === 'true';
  var transparentEnabled = header.getAttribute('data-transparent') === 'true';
  var HIDE_AFTER = 320;
  var SOLID_AFTER = 24;

  var lastY = window.scrollY || 0;
  var panelOpenCount = 0;
  var ticking = false;

  function setState() {
    var y = window.scrollY || 0;
    var top = transparentEnabled && isHome;

    header.classList.toggle('yv-header--top', top && y <= SOLID_AFTER);
    header.classList.toggle('yv-header--scrolled', y > SOLID_AFTER);

    if (!top) {
      header.classList.add('yv-header--solid');
    } else {
      header.classList.toggle('yv-header--solid', y > SOLID_AFTER);
    }

    if (panelOpenCount === 0) {
      var goingDown = y > lastY;
      header.classList.toggle('yv-header--hidden', goingDown && y > HIDE_AFTER);
    }

    lastY = y;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(setState);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  setState();

  /* ---- Drawers: search panel, mobile menu panel, cart drawer ---- */
  var overlay = document.querySelector('[data-yv-overlay]');
  var openTriggers = document.querySelectorAll('[data-yv-open]');
  var closeTriggers = document.querySelectorAll('[data-yv-close]');
  var activePanel = null;
  var lastFocused = null;

  function trapFocus(e) {
    if (!activePanel || e.key !== 'Tab') return;
    var focusable = activePanel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' && activePanel) closePanel(activePanel);
    trapFocus(e);
  }

  function openPanel(panel) {
    if (!panel || panel.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    panel.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('yv-scroll-lock');
    header.classList.add('yv-header--panel-open');
    panelOpenCount++;
    activePanel = panel;

    document.addEventListener('keydown', onKeydown);

    var focusTarget = panel.querySelector('[data-yv-autofocus]') || panel.querySelector('a, button, input');
    if (focusTarget) focusTarget.focus();
  }

  function closePanel(panel) {
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('yv-scroll-lock');
    header.classList.remove('yv-header--panel-open');
    panelOpenCount = Math.max(0, panelOpenCount - 1);
    activePanel = null;

    document.removeEventListener('keydown', onKeydown);
    setState();

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  openTriggers.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var targetId = btn.getAttribute('data-yv-open');
      var panel = document.getElementById(targetId);
      openPanel(panel);
    });
  });

  closeTriggers.forEach(function (btn) {
    btn.addEventListener('click', function () {
      closePanel(btn.closest('.yv-drawer, .yv-search-panel, .yv-modal'));
    });
  });

  if (overlay) {
    overlay.addEventListener('click', function () {
      if (activePanel) closePanel(activePanel);
    });
  }

  /* ---- Cart count ----
     Re-dispatch on ajax add-to-cart via: document.dispatchEvent(new
     CustomEvent('yvee:cart:updated', { detail: { count } })) — see README. */
  document.addEventListener('yvee:cart:updated', function (e) {
    var countEls = document.querySelectorAll('[data-yv-cart-count]');
    var count = e.detail && typeof e.detail.count === 'number' ? e.detail.count : null;

    if (count === null) {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { updateCountEls(countEls, cart.item_count); })
        .catch(function () {});
    } else {
      updateCountEls(countEls, count);
    }
  });

  function updateCountEls(els, count) {
    els.forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ---- Predictive search ----
     Debounced fetch against Shopify's /search/suggest.json endpoint,
     rendered into the search panel. Falls back to a normal search-page
     submit (the form's default behavior) if JS fails or on Enter. */
  var searchInput = document.querySelector('.yv-search-panel__input');
  var searchResults = document.querySelector('[data-yv-search-results]');
  var searchDebounce = null;

  if (searchInput && searchResults) {
    searchInput.addEventListener('input', function () {
      var term = searchInput.value.trim();
      window.clearTimeout(searchDebounce);

      if (term.length < 2) {
        searchResults.innerHTML = '';
        return;
      }

      searchDebounce = window.setTimeout(function () {
        fetch(
          '/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product,page,article' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last'
        )
          .then(function (r) { return r.json(); })
          .then(function (data) { renderPredictiveResults(data, term); })
          .catch(function () {});
      }, 200);
    });
  }

  function renderPredictiveResults(data, term) {
    var resources = data && data.resources && data.resources.results;
    if (!resources) return;

    var products = resources.products || [];
    var pages = (resources.pages || []).concat(resources.articles || []);

    if (!products.length && !pages.length) {
      searchResults.innerHTML = '<p class="yv-product__note">No results for &ldquo;' + escapeHtml(term) + '&rdquo;.</p>';
      return;
    }

    var html = '';
    if (products.length) {
      html += '<div class="yv-search-page__section-title">Products</div>';
      products.forEach(function (product) {
        html += '<a class="yv-search-page__result-link" href="' + product.url + '">' + escapeHtml(product.title) + '</a>';
      });
    }
    if (pages.length) {
      html += '<div class="yv-search-page__section-title">Pages</div>';
      pages.forEach(function (page) {
        html += '<a class="yv-search-page__result-link" href="' + page.url + '">' + escapeHtml(page.title) + '</a>';
      });
    }
    searchResults.innerHTML = html;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
})();
